import {GoogleOAuthProvider, useGoogleOneTapLogin} from '@react-oauth/google'
import {Divider, message} from 'antd'
import {auth_login_Api} from 'apis'
import classNames from 'classnames'
import {LoginContext} from 'components/LoginContext'
import {isDevelop} from 'envs/ForDevelop'
import {setTokenKey} from 'helpers/localStorage'
import jwt_decode from 'jwt-decode'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {Link, useLocation} from 'react-router-dom'
import {useToggle} from 'react-use'
import {loginSuccess} from 'redux/actions'
import UseState from '../../components/UseState'
import {APP_NAME, GG_ID, ROOT_URL} from '../../envs/_current/config'
import {useAppConfig} from '../../modules/local/AppConfigContext'
import GoogleLoginBtn from './GoogleLoginBtn/index'
import './Login.css'
import LoginForm from './LoginForm'
import Register from './RegisterForm'
import {getRouteSearchParams} from "../../envs/_current/history";
import ZaloLoginBtn from "./ZaloLoginBtn/index";
import ForDevelop from "../../envs/ForDevelop";
import AppleLoginBtn from "./AppleLoginBtn/index";
import {NullAsDefault, renderIf, renderIfElse} from "../Shared";
import Toggle from "../../components/Toggle";
import {GoDotFill} from "react-icons/go";
import {MdOutlineAlternateEmail} from "react-icons/md";
import {useHistory} from "../../modules/navigation/useRouter";

function toBase64(text) {
    return Buffer.from(text).toString('base64')
}

function fromBase64(text, encoding = 'utf8') {
    return Buffer.from(text, 'base64').toString(encoding)
}

export const useGoogleSignIn = ({client_id, redirect_uri}) => {
    const history = useHistory()

    const location = useLocation()

    const dispatch = useDispatch()

    const isMounted = useRef(true)

    const fragmentString = location.hash.substring(1)

    const {handleAsyncAction} = useAsyncAction({
        apiInfo: auth_login_Api,
        onSuccess: (__, res) => {
            if (!isMounted.current) return;
            const action = loginSuccess(res?.response?.data)
            dispatch(action)
        },
    })

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!fragmentString) return;

        const params = Object.fromEntries(new URLSearchParams(fragmentString));
        let state;
        try {
            state = params['state'] ? JSON.parse(fromBase64(params['state'])) : {};
        } catch (e) {
            state = {};
        }

        const { type, callback_url } = state ?? {};
        const token = params['access_token'];

        if (type === 'request_token' && token) {
            setTokenKey(token);
            signIn(token);

            // Tránh push liên tục hoặc push null
            if (history && callback_url && callback_url !== window.location.pathname) {
                history.replace(callback_url);
            }
        }
    }, [fragmentString]);

    function signIn(token) {
        if (token) {
            handleAsyncAction({
                token,
                provider: 'google',
            })
        }
    }

    /*
     * Create form to request access token from Google's OAuth 2.0 server.
     */
    function oauth2SignIn(options) {
        const oauth2Endpoint = 'https://accounts.google.com/o/oauth2/v2/auth';

        const searchParams = getRouteSearchParams();
        const stateData = {
            type: 'request_token',
            callback_url: window.location.pathname + (searchParams.keys.length
                ? '?' + searchParams.toString()
                : window.location.search),
        };

        const params = {
            client_id,
            redirect_uri,
            state: toBase64(JSON.stringify(stateData)),
            response_type: 'token',
            include_granted_scopes: 'true',
            scope: 'https://www.googleapis.com/auth/userinfo.email',
            ...(options ?? {}),
        };

        const query = new URLSearchParams(params).toString();
        window.location.assign(`${oauth2Endpoint}?${query}`);
    }

    return {
        signIn,
        oauth2SignIn,
    }
}

const GoogleOneTapHandler = ({children, client_id, redirect_uri}) => {
    const login = useContext(LoginContext)
    const isMounted = useRef(true)
    // ✅ Thêm state để disable hook khi sắp unmount
    const [disabled, setDisabled] = React.useState(!!login)

    const {oauth2SignIn} = useGoogleSignIn({
        client_id,
        redirect_uri,
    })

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            setDisabled(true); // ✅ disable hook trước khi unmount
        };
    }, []);

    useGoogleOneTapLogin({
        disabled: disabled || !!login, // ✅ dùng local disabled state
        onSuccess: (credentialResponse) => {
            if (!isMounted.current) return;
            if (!credentialResponse?.credential) return;
            try {
                const info = jwt_decode(credentialResponse.credential);
                oauth2SignIn({ login_hint: info?.email });
            } catch (e) {
                console.error('Google decode failed', e);
            }
        },
        onError: () => console.log('Google One Tap Failed'),
        promptMomentNotification: isDevelop()
            ? (n) => {
                if (n.isNotDisplayed() || n.isSkippedMoment()) {
                    document.cookie = `g_state=;path=/;expires=Mon, 01 Jan 1900 00:00:00 GMT`;
                }
            }
            : undefined,
    });

    useEffect(() => {
        return () => {
            try {
                document.cookie = `g_state=;path=/;expires=Mon, 01 Jan 1900 00:00:00 GMT`;

                const script = document.querySelector('script[src*="gsi/client"]');
                if (script) script.remove();

                const googleDiv = document.getElementById('g_id_onload');
                if (googleDiv) googleDiv.remove();
            } catch (e) {
                console.warn('Google One Tap cleanup failed', e);
            }
        };
    }, []);

    return children
}

export const GoogleOneTap = ({children}) => (
    <GoogleOAuthProvider clientId={GG_ID}>
        <GoogleOneTapHandler
            redirect_uri={ROOT_URL}
            client_id={GG_ID}>
            {children}
        </GoogleOneTapHandler>
    </GoogleOAuthProvider>
)

const Footer = () => {
    const t = useTranslate()

    return (
        <div className="mt-3 text-center text-xs text-color-500 italic">
            {t('by using Kristo Network, you agree to our')}{' '}
            <Link
                to={'/helps/privacy-policy'}
                className="font-medium text-color-100 no-underline">
                {t('privacy policy')}
            </Link>{' '}
            <span className="lowercase">{`${t('and')} `}</span>
            <Link
                to={'/helps/terms-of-service'}
                className="font-medium text-color-100 no-underline">
                {t('terms of service')}
            </Link>
            .
        </div>
    )
}


function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

const Login = ({
                   onSuccess = NullAsDefault,
                   isRequired = true,
                   isSignUpDefault = false,
                   initOrgType = undefined,
               }) => {
    const [isSignUp, signUpToggle] = useToggle(isSignUpDefault)
    const t = useTranslate()
    const dispatch = useDispatch()
    const {isLoading, handleAsyncAction} = useAsyncAction({
        apiInfo: auth_login_Api,
        onSuccess: (...args) => {
            dispatch(loginSuccess(_.get(args, '1.response.data')))
            onSuccess()
        },
        onError: (...args) => message.error(_.get(args, '1.message')),
    })

    const {org_types = {}} = useAppConfig('org_types')
    let user_types = []
    let organization_types = []
    user_types.push({
        id: 0,
        label: t('individual'),
        className: 'border border-color-50 hover:border-color-base text-color-100 background-100 hover:background-200',
    })
    Object.keys(org_types)
        .sort()
        .map((e) => {
            organization_types.push({
                id: e,
                label: org_types[e],
                className: 'bg-blue-50 hover:bg-blue-100 text-blue-600',
            })
        })

    return (
        <div className="flex flex-col gap-3">
            {!isSignUp && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col items-center gap-1 pt-2">
                        <span className="font-bold text-xl text-color-000 tracking-tight">
                            {t('login on')}
                            <span className="ml-1.5 text-primary">{'Kristo Network'}</span>
                        </span>
                    </div>
                    <div className="px-0 md:px-3 flex flex-col gap-3">
                        <GoogleLoginBtn onSuccess={handleAsyncAction}/>
                        {renderIf(
                            isIOS(),
                            <AppleLoginBtn onSuccess={handleAsyncAction}/>
                        )}
                        <ForDevelop>
                            <ZaloLoginBtn onSuccess={handleAsyncAction}/>
                        </ForDevelop>
                        <Divider style={{marginBottom: 0, color: 'var(--text-color-500)', fontSize: '0.75rem'}}
                                 className="lowercase">
                            {t('or')}
                        </Divider>
                        <Toggle>
                            {(isToggle, toggle) => renderIfElse(
                                isToggle,
                                <div className="flex flex-col gap-3">
                                    <LoginForm/>
                                    <div
                                        onClick={() => {
                                            signUpToggle()
                                        }}
                                        className="text-center text-sm text-color-400 hover:text-primary cursor-pointer">
                                        {t('register an account on Kristo Network')}
                                    </div>
                                </div>,
                                <button
                                    onClick={toggle}
                                    className="w-full flex flex-center gap-2.5 background border border-color-100 rounded-lg hover:border-color-300 hover:shadow-sm transition-all cursor-pointer"
                                    style={{
                                        minHeight: 44,
                                        fontSize: 14,
                                        fontWeight: '500',
                                        letterSpacing: '0.02em',
                                    }}>
                                    <MdOutlineAlternateEmail size={16} className="text-color-300"/>
                                    <span className="text-color-200 leading-none">
                                        {t('Continue with email')}
                                    </span>
                                </button>
                            )}
                        </Toggle>
                        <Footer/>
                    </div>
                </div>
            )}

            {isSignUp && (
                <div className="mb-2 flex flex-col gap-4">
                    <div className="flex flex-col items-center gap-1 pt-2">
                        <span className="font-bold text-xl text-color-000 tracking-tight">
                            {t('register an account')}
                            <span className="ml-1.5 text-primary">{'Kristo Network'}</span>
                        </span>
                    </div>
                    <div className="px-0 md:px-3 flex flex-col gap-3">
                        <div
                            onClick={() => {
                                signUpToggle()
                            }}
                            className="text-center text-sm text-color-400 cursor-pointer">
                            {`${t('if you already have a Kristo Network account')}. ${t('please')}`}
                            <span className="ml-1 font-medium text-primary hover:underline">
                              {`${t('login')}`}
                            </span>
                        </div>
                        <UseState
                            initialValue={{
                                step: !!!initOrgType ? 0 : 1,
                                org_type: Number(initOrgType),
                            }}>
                            {([values, setValues]) => {
                                const {step} = values
                                switch (step) {
                                    case 0:
                                        return (
                                            <section className="flex flex-col justify-center gap-3">
                                                <div className="grid grid-cols-1 gap-3">
                                                    <div className="text-sm text-color-300 italic">
                                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                                        {t('Register as a member of {app_name}', {
                                                            app_name: (
                                                                <span className="font-semibold text-primary">
                                                                    {APP_NAME}
                                                                </span>
                                                            ),
                                                        })}
                                                        <span className="ml-1">{`, ${t('as role')} :`}</span>
                                                    </div>
                                                    {user_types.map((e, i) => (
                                                        <div
                                                            key={i}
                                                            onClick={() => {
                                                                if (e.id === 0) {
                                                                    setValues({
                                                                        ...values,
                                                                        step: 1,
                                                                    })
                                                                } else {
                                                                    setValues({
                                                                        ...values,
                                                                        org_type: e.id,
                                                                        step: 1,
                                                                    })
                                                                }
                                                            }}
                                                            style={{padding: '0.625rem'}}
                                                            className={classNames('w-full flex flex-center text-base font-semibold rounded-lg cursor-pointer', e.className)}>
                                                            {t(e.label)}
                                                        </div>
                                                    ))}
                                                    <div className="text-sm text-color-300 italic">
                                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                                        {t('Or register to create an organization/group/association')}
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        {organization_types.map((e, i) => (
                                                            <div
                                                                key={i}
                                                                onClick={() => {
                                                                    if (e.id === 0) {
                                                                        setValues({
                                                                            ...values,
                                                                            step: 1,
                                                                        })
                                                                    } else {
                                                                        setValues({
                                                                            ...values,
                                                                            org_type: e.id,
                                                                            step: 1,
                                                                        })
                                                                    }
                                                                }}
                                                                style={{padding: '0.5rem'}}
                                                                className={classNames(
                                                                    'w-full flex flex-center font-semibold hover:scale-xs rounded-xl cursor-pointer capitalize',
                                                                    e.className
                                                                )}>
                                                                {t(e.label)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </section>
                                        )
                                    case 1:
                                        return !!values.org_type ? (
                                            <section className="flex flex-col justify-center gap-3">
                                                <div className="space-x-1 text-sm text-color-300 italic 11-11-11">
                                                    <span className="font-semibold">{`* ${t('note')} : `}</span>
                                                    {t('register note description')}
                                                </div>
                                                <Register
                                                    setBack={() => {
                                                        setValues({
                                                            ...values,
                                                            org_type: undefined,
                                                            step: 0,
                                                        })
                                                    }}
                                                    {...values}
                                                />
                                            </section>
                                        ) : (
                                            <div className="flex flex-col gap-4">
                                                <div className="flex flex-col gap-2">
                                                    <div className="text-sm text-color-300 italic">
                                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                                        {`${t('you can register a Kristo Network account using')} : `}
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                        <section>
                                                            <GoogleLoginBtn onSuccess={handleAsyncAction}
                                                                            label="register with gmail"/>
                                                        </section>
                                                        {renderIf(
                                                            isIOS(),
                                                            <section>
                                                                <AppleLoginBtn onSuccess={handleAsyncAction}
                                                                               label="register with apple id"/>
                                                            </section>
                                                        )}
                                                        <ForDevelop>
                                                            <section>
                                                                <ZaloLoginBtn onSuccess={handleAsyncAction}
                                                                              label="register with zalo"/>
                                                            </section>
                                                        </ForDevelop>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <div className="text-sm text-color-300 italic">
                                                        <GoDotFill size={10} className="mr-1 text-color-500"/>
                                                        {`${t('with your email')} : `}
                                                    </div>
                                                    <Register
                                                        setBack={() => {
                                                            setValues({
                                                                ...values,
                                                                org_type: undefined,
                                                                step: 0,
                                                            })
                                                        }}
                                                        {...values}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    default:
                                        return null
                                }
                            }}
                        </UseState>
                    </div>
                </div>
            )}
            {!isRequired && (
                <div
                    className="flex flex-center cursor-pointer text-color-400 hover:text-color-000"
                    onClick={() => {
                        onSuccess()
                    }}>
                    {t('skip')}
                </div>
            )}
        </div>
    )
}
export default Login